import { AxiosRequestConfig } from "axios";
import { ApiResponse, DocumentResponse, BackendResponse } from "../models/api-response";
import { callExternalApi } from "./external-api.service";
import { getLogger } from "src/utils/logger";
import { BehaviourSupportPlanData, PersonalisedLearningSupportPlanData, BehaviourResponsePlanData } from "../models/form-types";
import { GENERIC_ERROR_MESSAGE } from "src/utils/errorMessages";

const logger = getLogger('document.service');

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;


/**
 * Submit behavior support plan data to generate a document
 * @param formData The behavior support plan form data
 * @param accessToken Optional access token for authentication
 * @returns API response with generated document or error
 */
export const submitBehaviourSupportPlan = async (
  formData: BehaviourSupportPlanData,
  accessToken?: string
): Promise<ApiResponse<DocumentResponse>> => {
  try {
    const config: AxiosRequestConfig = {
      url: `${apiServerUrl}/api/documents/behaviourSupportPlan`,
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      data: formData,
    };

    logger.debug(`Submitting behaviour support plan data`);
    
    const response = await callExternalApi<BackendResponse>({ config });
    
    if (response.error || !response.data) {
      logger.error("Failed to generate behaviour support plan document", 
        response.error || new Error("No data returned"));
      return {
        data: null,
        error: response.error || { message: "No data returned from server" }
      };
    }
        
    // Check response status
    if (response.data.status !== "success") {
      const errorMessage = response.data.message || "Failed to generate behaviour support plan document";
      logger.error(errorMessage);
      return {
        data: null,
        error: { message: errorMessage }
      };
    }
    
    const docResponse: DocumentResponse = {
      documentId: response.data.document_id,
      docBase64: response.data.content.doc_base64
    };
    
    return {
      data: docResponse,
      error: null
    };
  } catch (error) {
    logger.error("Unexpected error in submitBehaviourSupportPlan", error);
    return {
      data: null,
      error: { 
        message: GENERIC_ERROR_MESSAGE
      }
    };
  }
};

/**
 * Submit personalised learning and support plan data to generate a document
 * @param formData The personalised learning and support plan form data
 * @param accessToken Optional access token for authentication
 * @returns API response with generated document or error
 */
export const submitPersonalisedLearningSupportPlan = async (
  formData: PersonalisedLearningSupportPlanData,
  accessToken?: string
): Promise<ApiResponse<DocumentResponse>> => {
  try {
    const config: AxiosRequestConfig = {
      url: `${apiServerUrl}/api/documents/personalisedLearningSupportPlan`,
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      data: formData,
    };

    logger.debug(`Submitting personalised learning and support plan data`);
    
    const response = await callExternalApi<BackendResponse>({ config });
    
    if (response.error || !response.data) {
      logger.error("Failed to generate personalised learning and support plan document", 
        response.error || new Error("No data returned"));
      return {
        data: null,
        error: response.error || { message: "No data returned from server" }
      };
    }
        
    // Check response status
    if (response.data.status !== "success") {
      const errorMessage = response.data.message || "Failed to generate personalised learning and support plan document";
      logger.error(errorMessage);
      return {
        data: null,
        error: { message: errorMessage }
      };
    }
    
    const docResponse: DocumentResponse = {
      documentId: response.data.document_id,
      docBase64: response.data.content.doc_base64
    };
    
    return {
      data: docResponse,
      error: null
    };
  } catch (error) {
    logger.error("Unexpected error in submitPersonalisedLearningSupportPlan", error);
    return {
      data: null,
      error: { 
        message: GENERIC_ERROR_MESSAGE
      }
    };
  }
};

/**
 * Submit behaviour response plan data to generate a document
 * @param formData The behaviour response plan form data
 * @param accessToken Optional access token for authentication
 * @returns API response with generated document or error
 */
export const submitBehaviourResponsePlan = async (
  formData: BehaviourResponsePlanData,
  accessToken?: string
): Promise<ApiResponse<DocumentResponse>> => {
  try {
    const config: AxiosRequestConfig = {
      url: `${apiServerUrl}/api/documents/behaviourResponsePlan`,
      method: "POST",
      headers: {
        "content-type": "application/json",
        ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
      },
      data: formData,
    };

    logger.debug(`Submitting behaviour response plan data`);
    
    const response = await callExternalApi<BackendResponse>({ config });
    
    if (response.error || !response.data) {
      logger.error("Failed to generate behaviour response plan document", 
        response.error || new Error("No data returned"));
      return {
        data: null,
        error: response.error || { message: "No data returned from server" }
      };
    }
        
    // Check response status
    if (response.data.status !== "success") {
      const errorMessage = response.data.message || "Failed to generate behaviour response plan document";
      logger.error(errorMessage);
      return {
        data: null,
        error: { message: errorMessage }
      };
    }
    
    const docResponse: DocumentResponse = {
      documentId: response.data.document_id,
      docBase64: response.data.content.doc_base64
    };
    
    return {
      data: docResponse,
      error: null
    };
  } catch (error) {
    logger.error("Unexpected error in submitBehaviourResponsePlan", error);
    return {
      data: null,
      error: { 
        message: GENERIC_ERROR_MESSAGE
      }
    };
  }
};
